import type { PreviewField } from '../../extensions';

export const MAX_DEPTH = 3;

export const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
};

export const getNestedValue = (
  obj: Record<string, unknown>,
  path: string,
): unknown => {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (!isPlainObject(current)) return undefined;
    current = current[part];
  }
  return current;
};

export const hasNestedKey = (
  obj: Record<string, unknown>,
  path: string,
): boolean => getNestedValue(obj, path) !== undefined;

export const humanizeKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export const isRenderableScalar = (
  value: unknown,
): value is string | number | boolean | null | undefined => {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
};

export const isScalarArray = (
  value: unknown,
): value is (string | number | boolean)[] =>
  Array.isArray(value) &&
  value.every(
    v =>
      typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
  );

export const pluralize = (count: number, singular: string): string =>
  count === 1 ? `1 ${singular}` : `${count} ${singular}s`;

const MCP_WRAPPER_KEYS = new Set(['body', 'query_params']);

export const resolveInputBody = (
  input: Record<string, unknown>,
): Record<string, unknown> => {
  if (!('body' in input)) return input;

  let parsedBody: Record<string, unknown>;

  if (typeof input.body === 'string') {
    try {
      const parsed = JSON.parse(input.body);
      if (isPlainObject(parsed)) parsedBody = parsed;
      else return input;
    } catch {
      return input;
    }
  } else if (isPlainObject(input.body)) {
    parsedBody = input.body;
  } else {
    return input;
  }

  const topLevel: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (!MCP_WRAPPER_KEYS.has(key) && value !== '') {
      topLevel[key] = value;
    }
  }

  return { ...parsedBody, ...topLevel };
};

const MAX_DEFAULT_PREVIEW_FIELDS = 8;

export const getDefaultPreviewFields = (
  inputObj: Record<string, unknown>,
): PreviewField[] => {
  const priorityFields: PreviewField[] = [];

  if ('displayName' in inputObj) {
    priorityFields.push({ key: 'displayName', label: 'Name' });
  } else if ('name' in inputObj) {
    priorityFields.push({ key: 'name', label: 'Name' });
  }

  if ('description' in inputObj) {
    priorityFields.push({ key: 'description', label: 'Description' });
  }

  const priorityKeySet = new Set(priorityFields.map(f => f.key));

  const remainingFields = Object.entries(inputObj)
    .filter(
      ([key, value]) =>
        !priorityKeySet.has(key) &&
        (isRenderableScalar(value) || isScalarArray(value)),
    )
    .map(([key]) => ({ key }));

  return [...priorityFields, ...remainingFields].slice(
    0,
    MAX_DEFAULT_PREVIEW_FIELDS,
  );
};
